import { useEffect, useState, useCallback } from "react";
import { createPortal } from "react-dom";
import api from "@/lib/api";
import { startPoll } from "@/lib/poll";
import { SentenceModal } from "@/components/SentenceModal";
import { DrawPotModal } from "@/components/DrawPotModal";
import { SentenceShopModal } from "@/components/SentenceShopModal";

const pad = (n) => String(n).padStart(2, "0");
export const fmtDuration = (secs) => {
  const s = Math.max(0, Math.floor(secs));
  return `${pad(Math.floor(s / 86400))}:${pad(Math.floor((s % 86400) / 3600))}:${pad(Math.floor((s % 3600) / 60))}:${pad(s % 60)}`;
};

function ClockBlock({ label, value, testId }) {
  return (
    <div className="flex flex-col items-center gap-2">
      <span className="text-[10px] uppercase tracking-widest" style={{ color: "var(--text-muted)" }}>{label}</span>
      <span className="font-head text-2xl sm:text-4xl tracking-widest" style={{ color: "var(--text-primary)" }} data-testid={testId}>{value}</span>
    </div>
  );
}

export function SentenceClock() {
  const [state, setState] = useState(null);
  const [now, setNow] = useState(Date.now());
  const [modal, setModal] = useState(null);
  const [draw, setDraw] = useState(false);
  const [shop, setShop] = useState(null);

  const load = useCallback(() => api.get("/sentence").then(({ data }) => setState({ ...data, fetchedAt: Date.now() })).catch((e) => console.debug("sentence", e)), []);
  useEffect(() => { load(); return startPoll(load, 15000); }, [load]);
  useEffect(() => { const id = setInterval(() => setNow(Date.now()), 1000); return () => clearInterval(id); }, []);

  const remaining = state?.active ? state.remaining_seconds - (now - state.fetchedAt) / 1000 : state?.remaining_seconds || 0;
  return (
    <div className="flex flex-col items-center mt-10 fade-in-up" data-testid="sentence-clock">
      <div className="flex flex-wrap items-start justify-center gap-10 sm:gap-20">
        <ClockBlock label="remaining" value={fmtDuration(remaining)} testId="sentence-remaining" />
        <ClockBlock label="starting" value={fmtDuration((state?.start_minutes || 0) * 60)} testId="sentence-starting" />
      </div>
      <span className="text-[10px] tracking-widest mt-2" style={{ color: "var(--text-muted)" }}>days : hours : minutes : seconds</span>
      {state?.paused && <span className="text-xs tracking-widest mt-3 px-3 py-1" style={{ border: "1px solid var(--accent)", color: "var(--accent)" }} data-testid="sentence-paused">clock paused</span>}
      <div className="grid grid-cols-2 gap-4 mt-10">
        <button onClick={() => setModal("reduce")} disabled={state?.paused} data-testid="sentence-reduce-btn" className="btn px-10 py-3 text-sm tracking-widest">REDUCE</button>
        <button onClick={() => setModal("extend")} disabled={state?.paused} data-testid="sentence-extend-btn" className="btn px-10 py-3 text-sm tracking-widest">EXTEND</button>
        <button onClick={() => setShop("help")} data-testid="sentence-help-btn" className="btn px-10 py-3 text-sm tracking-widest">HELP</button>
        <button onClick={() => setShop("punish")} data-testid="sentence-punish-btn" className="btn px-10 py-3 text-sm tracking-widest">PUNISH</button>
      </div>
      <div className="flex flex-col items-center gap-2 mt-12">
        <span className="text-[10px] uppercase tracking-widest" style={{ color: "var(--text-muted)" }}>the pot</span>
        <span className="font-head text-xl sm:text-2xl tracking-widest" style={{ color: "var(--accent)" }} data-testid="sentence-pot">
          ${(state?.pot_cad || 0).toFixed(2)} CAD
        </span>
      </div>
      <button onClick={() => setDraw(true)} data-testid="draw-pot-btn" className="btn px-10 py-3 mt-6 text-sm tracking-widest">DRAW ON THE POT</button>
      {draw && createPortal(<DrawPotModal onClose={() => setDraw(false)} onUpdated={load} />, document.body)}
      {shop && createPortal(<SentenceShopModal side={shop} onClose={() => setShop(null)} />, document.body)}
      {modal && createPortal(<SentenceModal direction={modal} state={state} remaining={remaining} onClose={() => setModal(null)} />, document.body)}
    </div>
  );
}
